import { StreamLanguage, type StringStream } from "@codemirror/language";
import { tags } from "@lezer/highlight";

type LmlContext = "normal" | "attrs" | "attrValue";

interface LmlState {
  context: LmlContext;
}

export const lmlLanguage = StreamLanguage.define<LmlState>({
  name: "lml",

  startState(): LmlState {
    return { context: "normal" };
  },

  token(stream: StringStream, state: LmlState): string | null {
    // Line comment: // to end of line
    if (stream.match("//")) {
      stream.skipToEnd();
      return "comment";
    }

    // String: "..."
    if (stream.match('"')) {
      while (!stream.eol()) {
        const ch = stream.next();
        if (ch === "\\") {
          stream.next(); // skip escaped char
        } else if (ch === '"') {
          break;
        }
      }
      return "string";
    }

    // Tag: @identifier
    if (stream.match(/@[a-zA-Z_][a-zA-Z0-9_-]*/)) {
      return "tagName";
    }

    // Brackets for attributes
    if (stream.match("[")) {
      state.context = "attrs";
      return "punctuation";
    }
    if (stream.match("]")) {
      state.context = "normal";
      return "punctuation";
    }

    // Inside attribute context
    if (state.context === "attrs") {
      // Attribute key=value
      if (stream.match(/^[a-zA-Z_][a-zA-Z0-9_-]*(?==)/)) {
        return "attributeName";
      }
      if (stream.match("=")) {
        return "punctuation";
      }
    }

    // Embedded LaTeX. LML content is frequently LaTeX, so a math span tints as
    // one object and a control sequence takes the command colour — the same
    // treatment they get in the LaTeX mode, so the two cannot disagree.
    if (stream.match(/^\$\$[\s\S]*?\$\$|^\$(?:\\[\s\S]|[^$\\])+\$/)) {
      return "string";
    }
    if (stream.match(/^\\(?:[^a-zA-Z()[\]]|[a-zA-Z*]+)/)) {
      return "keyword";
    }

    // Braces
    if (stream.match("{") || stream.match("}")) {
      return "punctuation";
    }

    // Numbers. Uncoloured: in LML they are almost always an attribute value
    // (level=1), and a hue of their own adds noise rather than meaning.
    if (stream.match(/^\d+\.?\d*/)) {
      return state.context === "attrs" ? "string" : "punctuation";
    }

    // Content run (prose / body) until next structural token — dual typography
    // in the source visualization pane (serif content, mono @tags).
    if (stream.match(/^[^@"$\[\]{}\\/\r\n]+/)) {
      return "content";
    }

    stream.next();
    return "content";
  },

  tokenTable: {
    comment: tags.comment,
    string: tags.string,
    tagName: tags.tagName,
    attributeName: tags.attributeName,
    keyword: tags.keyword,
    punctuation: tags.punctuation,
    content: tags.content,
  },
});
